import { ReferentialKey } from "api/referential";
import { useCallback, useRef, useState } from "react";
import { selectReferentialDataByKey } from "features/referentials/selectors/selectReferentialDataByKey";
import { useAppSelector } from "app/hooks";
import { SEARCH_DELAY } from "components/domain/SearchBar/constants";
import { createSearchFunctions } from "components/domain/SearchBar/searchFunctions";
import { createTransformFunctions } from "components/domain/SearchBar/transformFunctions";
import {
  UseSearchPersonParams,
  UseSearchPersonReturn,
  BaseSearchResult,
  SearchFeatureFilter,
  SearchPersonResult
} from "components/domain/SearchBar/types";

export const useSearchPerson = <T extends BaseSearchResult = SearchPersonResult>({
  withNaturalPerson = true,
  withLegalEntity = true,
  onSelect,
  featureFilter = SearchFeatureFilter.Default,
  excludeFilter,
  plateformeId,
}: UseSearchPersonParams<T>): UseSearchPersonReturn<T> => {
  const referentialData = useAppSelector(selectReferentialDataByKey(ReferentialKey.Civility));
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const searchFunctions = createSearchFunctions();
  const searchFunction = searchFunctions[featureFilter];

  const transformFunctions = createTransformFunctions<T>();
  const defaultTransformer = transformFunctions[featureFilter];
  const transformer = defaultTransformer;

  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsLoading(false);
      setIsOpen(false);
      return;
    }

    try {
      setIsLoading(true);

      const response = await searchFunction(query, {
        withNaturalPerson,
        withLegalEntity,
        plateformeId
      });

      if (response) {
        let transformedResults = response.items
          .map((item) => transformer(item, referentialData))
          .filter((result): result is T => result !== null);

        // Apply exclude filter if provided
        if (excludeFilter) {
          const { field, values } = excludeFilter;
          transformedResults = transformedResults.filter(
            (result) => !values.includes(result[field] as string | number | boolean)
          );
        }

        setSearchResults(transformedResults);
        setTotalCount(response.totalCount);
        setIsOpen(true);
      }
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      setSearchResults([]);
      setTotalCount(0);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [withNaturalPerson, withLegalEntity, referentialData, transformer, searchFunction, excludeFilter, plateformeId]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);

    if (selectedItem && value !== selectedItem.name) {
      setSelectedItem(null);
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim() === "") {
      setSearchResults([]);
      setTotalCount(0);
      setIsLoading(false);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    setIsOpen(true);

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, SEARCH_DELAY);
  }, [performSearch, selectedItem]);

  const cleanupTimeout = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    cleanupTimeout();
  }, [cleanupTimeout]);

  const handleSelectItem = useCallback((item: T | null) => {
    if (!item) return;
    onSelect(item);

    setSelectedItem(item);
    setSearchText(item.name || "");
    setIsOpen(false);
    setSearchResults([]);
  }, [onSelect]);

  return {
    searchResults,
    searchText,
    isLoading,
    isOpen,
    totalCount,
    selectedItem,
    handleOpen,
    handleSearchChange,
    handleClose,
    handleSelectItem
  };
};
